import type { BlotterEntryStatus } from '@/store/blotter/api/types/BlotterEntryStatus.ts';
import {
  type BlotterAccumulatorProductType,
  type BlotterTradeEntry,
  isAccumulatorProductType,
} from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import type { BlotterEntry } from '@/store/blotter/types/BlotterEntry.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import { parseDecimal } from '@/utils/number.ts';

export type BlotterAccumulatorEntry = BlotterEntry & {
  //
  // technical fields
  //
  status: BlotterEntryStatus;
  execTime: ISODateTime; // "exec time" or "trade date" in legacy fx, depending on the context ...
  updateTime: ISODateTime;

  // who
  account: string;
  portfolio: string;
  clientId: number;
  sales: string;
  venue: string;

  // what
  product: BlotterAccumulatorProductType;
  currencyPair: CurrencyPair;
  fixingFrequency: string | null;
  settlementFrequency: string | null;
  koConventionType: string | null;
  koTime: ISODateTime | null;
  premium: number | null;
  way:
    | 'Accu' // sole value observed on devci
    | 'Sell'
    | 'Buy'
    | 'Both'; // (other grids)
  expiry: ISODate;
  premiumCurrency: Currency | '' | null;
  premiumSide: 'Pay' | 'Receive' | '' | null;
  dealtAmount: number;
  dealtCurrency: Currency;
};

/**
 * Converts a Trade entry coming from webapi to a blotter accumulator entry exploitable by the front.
 * The input is expected to be of blotter type 'Accumulator', will blow otherwise.
 * @param trade A trade entry coming from webapi
 * @returns a blotter accumulator entry exploitable by the front
 */
export const convertAccumulatorEntry = (trade: BlotterTradeEntry): BlotterAccumulatorEntry | null => {
  if (trade.blotterType !== 'Accumulator') {
    throw Error('dev error');
  }
  if (!isAccumulatorProductType(trade.product)) {
    // this mean the blotterType and the product field of the trade does not agree
    console.error(`convertAccumulatorEntry: unsupported product type "${trade.product}" for trade id=${trade.id}`);
    return null;
  }
  return {
    clientId: trade.counterpartyBdrId,
    account: trade.accountLongName ?? trade.account,
    execTime: trade.date,
    sales: trade.salesFullName,

    // unchanged
    id: trade.id,
    updateTime: trade.updateTime,
    status: trade.status,
    currencyPair: trade.currencyPair,
    portfolio: trade.portfolio,
    fixingFrequency: trade.fixingFrequency,
    settlementFrequency: trade.settlementFrequency,
    koConventionType: trade.koConventionType,
    koTime: trade.koTime,
    premium: parseDecimal(trade.premium),
    product: trade.product,
    way: trade.way,
    venue: trade.venue,
    expiry: trade.expiry,
    premiumCurrency: trade.premiumCurrency,
    premiumSide: trade.premiumSide,
    dealtCurrency: trade.dealtCurrency,
    dealtAmount: trade.dealtAmount,
  };
};
